// /Frontend/hoppla-admin/src/features/auth/auth-storage.ts

export type AdminRole = 'SUPER_ADMIN' | 'OPS_ADMIN' | 'BILLING_ADMIN' | 'READ_ONLY';

export interface StoredAdmin {
  id: string;
  username: string;
  role: AdminRole;
}

export interface StoredAdminSession {
  sessionId?: string | null;
  admin: StoredAdmin;
}

const STORAGE_KEYS = [
  'hoppla-admin-auth',
  'hoppla-admin-auth-session',
  'hoppla-admin-session',
] as const;

function safeParse<T>(value: string | null): T | null {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function readStoredAdminSession(): StoredAdminSession | null {
  for (const key of STORAGE_KEYS) {
    const currentSessionValue = safeParse<StoredAdminSession>(window.sessionStorage.getItem(key));

    if (currentSessionValue?.admin?.id) {
      return currentSessionValue;
    }

    const legacyValue = safeParse<StoredAdminSession>(window.localStorage.getItem(key));

    if (legacyValue?.admin?.id) {
      window.sessionStorage.setItem(key, JSON.stringify(legacyValue));
      window.localStorage.removeItem(key);
      return legacyValue;
    }
  }

  return null;
}

export function writeStoredAdminSession(session: StoredAdminSession): void {
  window.sessionStorage.setItem(STORAGE_KEYS[0], JSON.stringify(session));
}

export function clearStoredAdminSession(): void {
  for (const key of STORAGE_KEYS) {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const decoded = window.atob(padded);

    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isJwtExpired(token: string, skewSeconds = 30): boolean {
  if (!token) {
    return true;
  }

  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  if (typeof exp !== 'number') {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
}
